import React, { useEffect, useState } from 'react';
import { Alert, Button, DatePicker, Drawer, Form, Input, InputNumber, Select } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { TransportRouteDetailDto } from '../../../dto/transport';
import { transportDatePickerProps } from '../../../components/transportFormUtils';
import { MAX_MONEY_AMOUNT } from '../../../utils/transportCostAllocation';
import { formatMoneyPl } from '../../../utils/currency';
import { toTransportInvoicePayload, type TransportInvoiceFormValues } from '../transportInvoice';
import type { TransportInvoicePayload } from '../api/transportRoutesApi';
import './TransportInvoice.css';

const CURRENCIES = ['PLN', 'EUR', 'USD'];

interface Props {
  open: boolean;
  transport: TransportRouteDetailDto;
  onClose: () => void;
  /** Resolves true when saved; the drawer then closes. */
  onSave: (payload: TransportInvoicePayload) => Promise<boolean>;
}

/** Faktura przewoźnika: one per truck; its amount replaces the planned rate. */
export default function TransportInvoiceDrawer({ open, transport, onClose, onSave }: Props) {
  const { t } = useTranslation(['transport', 'common']);
  const [form] = Form.useForm<TransportInvoiceFormValues>();
  const [saving, setSaving] = useState(false);
  const invoice = transport.invoice;

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      invoiceNumber: invoice?.number ?? transport.invoiceNumber ?? '',
      invoiceIssueDate: invoice?.issueDate ? dayjs(invoice.issueDate) : null,
      // A first invoice starts empty: the plan must not be saved as an
      // invoice by one click (review PR #380). A correction keeps its amount.
      invoiceAmount: invoice?.amount ?? null,
      invoiceCurrency: invoice?.currency ?? transport.currency ?? 'PLN',
    } as TransportInvoiceFormValues);
  }, [open, invoice, transport.invoiceNumber, transport.currency, form]);

  const submit = async (values: TransportInvoiceFormValues) => {
    if (saving) return;
    setSaving(true);
    try {
      if (await onSave(toTransportInvoicePayload(values))) onClose();
    } finally {
      setSaving(false);
    }
  };

  const plan = invoice
    ? (invoice.plannedRate == null ? null : formatMoneyPl(invoice.plannedRate, invoice.plannedCurrency || 'PLN'))
    : (transport.transportRate == null ? null : formatMoneyPl(transport.transportRate, transport.currency || 'PLN'));

  return (
    <Drawer
      title={invoice ? t('transport:invoice.editTitle') : t('transport:invoice.addTitle')}
      open={open}
      onClose={onClose}
      width={560}
      maskClosable={false}
      destroyOnClose
      footer={(
        <div className="transport-invoice-drawer__actions">
          <Button onClick={onClose}>{t('common:cancel')}</Button>
          <Button type="primary" loading={saving} onClick={() => form.submit()} data-testid="transport-invoice-save">
            {t('transport:invoice.save')}
          </Button>
        </div>
      )}
    >
      <div className="transport-invoice-drawer">
        <p className="transport-invoice-drawer__lead">
          {plan ? t('transport:invoice.replacesPlan', { plan }) : t('transport:invoice.replacesPlanNoPlan')}
        </p>
        {transport.status !== 'DELIVERED' ? (
          <Alert type="info" showIcon message={t('transport:invoice.notDelivered')} className="transport-invoice-drawer__alert" />
        ) : null}
        <Form form={form} layout="vertical" onFinish={submit} requiredMark data-testid="transport-invoice-form">
          <div className="transport-invoice-drawer__grid">
            <Form.Item
              name="invoiceNumber"
              label={t('transport:invoice.number')}
              rules={[{ required: true, whitespace: true, max: 100, message: t('transport:invoice.numberRequired') }]}
            >
              <Input autoComplete="off" />
            </Form.Item>
            <Form.Item
              name="invoiceIssueDate"
              label={t('transport:invoice.issueDate')}
              rules={[{ required: true, message: t('transport:invoice.issueDateRequired') }]}
            >
              <DatePicker {...transportDatePickerProps} />
            </Form.Item>
            <Form.Item
              name="invoiceAmount"
              label={t('transport:invoice.amount')}
              rules={[{
                type: 'number', required: true, min: 0, max: MAX_MONEY_AMOUNT,
                message: t('transport:invoice.amountRequired', { max: MAX_MONEY_AMOUNT }),
              }]}
            >
              <InputNumber decimalSeparator="," precision={2} className="transport-invoice-drawer__amount" />
            </Form.Item>
            <Form.Item name="invoiceCurrency" label={t('transport:invoice.currency')} rules={[{ required: true }]}>
              <Select options={CURRENCIES.map((currency) => ({ value: currency, label: currency }))} />
            </Form.Item>
          </div>
        </Form>
      </div>
    </Drawer>
  );
}
